import {combine, sample} from 'effector';
import i18next from 'i18next';
import {toast} from 'react-toastify';

import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';

import {
    createOrder,
    fetchGtinInfoEvent,
    file$,
    fileChanged,
    firstStep$,
    firstStepChanged,
    getGtinInfoFx,
    OrdersCreateGate,
    productGroup$,
    productGroupChanged,
    products$,
    productsChanged,
    sendOrderFx,
    step$,
    stepChanged,
} from './index';

firstStep$
    .on(firstStepChanged, (state, payload) => ({
        ...state,
        ...payload,
    }))
    .reset(OrdersCreateGate.close);
step$.on(stepChanged, (_, payload) => payload).reset(OrdersCreateGate.close);
file$.on(fileChanged, (_, payload) => payload).reset(OrdersCreateGate.close);
productGroup$.on(productGroupChanged, (_, payload) => payload).reset(OrdersCreateGate.close);
products$.on(productsChanged, (_, payload) => payload).reset(OrdersCreateGate.close);

sendOrderFx.fail.watch(({error}) => showRestError(error as RestError));

sendOrderFx.done.watch(() => {
    history.push(ROUTE.ORDERS_GRID);
    toast.success(i18next.t('pages.orders.form.orderCreated'));
});

getGtinInfoFx.fail.watch(({error}) => showRestError(error as RestError));

getGtinInfoFx.done.watch((data: {result: any}) => {
    const products = products$.getState().map((item) => ({
        productName: data?.result?.[item.gtin || '']?.productName,
        productGroupCode: data?.result?.[item.gtin || '']?.productGroupCode,
        productVersionId: data?.result?.[item.gtin || '']?.productId,
        ...(data?.result?.[item.gtin || '']?.productGroupCode === 'MEDICATIONS' && {
            manufacturingDate: item.manufacturingDate ?? new Date().toISOString(),
            expirationDate: item.expirationDate ?? '',
            batchNumber: item.batchNumber ?? '',
        }),
        ...item,
    }));
    productsChanged(products);

    const hasDifferentPackageTypes =
        new Set(products.map(({type}) => type).filter(Boolean)).size > 1;
    if (
        hasDifferentPackageTypes ||
        products.some(
            ({productGroupCode, expirationDate, batchNumber}) =>
                productGroupCode === 'MEDICATIONS' && (!expirationDate || !batchNumber),
        )
    ) {
        stepChanged(1);
    }
});

sample({
    clock: createOrder,
    source: combine({
        firstStep: firstStep$,
        products: products$,
    }),
    fn: ({firstStep, products}) => {
        const payload = {
            orderType: firstStep.productTypes === 'import' ? 'IMPORT' : 'LOCAL',
            manufacturerCountryCode: firstStep.country?.value,
            manufacturerName: firstStep.manufName,
            manufacturerAddress: firstStep.manufAddress,
        };
        const localType = {
            factoryId: firstStep.factoryId?.value,
            productionLineId: firstStep.productionLineId?.value,
        };

        return {
            ...payload,
            ...(firstStep.productTypes === 'local' && localType),
            products,
        };
    },
    target: sendOrderFx,
});

sample({
    clock: fetchGtinInfoEvent,
    source: products$,
    target: getGtinInfoFx,
});

export const orderProccessing$ = combine(sendOrderFx.pending, (pending) => pending);
